import { VatCondition } from "../../modules/administration"
import { FiscalDocumentClass, FiscalDocumentDetails, FiscalDocumentStatus, FiscalDocumentType, Discount } from "../../modules/fiscal-documents"
import { InvoiceItemQuantity, InvoiceMode } from "../../modules/sales/state/types"
import { IdentifierType } from "../../modules/system"
import { Address } from "../../features/address/types"
import type { AdjustmentNote } from "../../modules/administration/state/types"
import type { HoardPriceCorrection } from "../../modules/sales/state/types"

// Discount on some units of an item, with VAT and the payment type surcharge
export type PriceAdjustmentPartBody = {
    itemId: string
    quantity: number
    unitAmount: number
    vatTaxYield: number
    total?: number
}

// The server splits the note between what is invoiced and what is not, and saves it with the operation in a single
// transaction. The amounts that need the price engine are computed here
export type ManualCreditNoteBody = {
    userId?: string
    reason: string
    // The adjusted amount the totals were computed with: the server rejects the note if it changed
    expectedAdjustedAmount: number
    // Operation total with perceptions and without adjustment notes
    operationTotal: number
    perceptionsAmount: number
    // Sale or product hoard
    parts?: PriceAdjustmentPartBody[]
    // Financial hoard
    amount?: number
}

export type ManualCreditNoteResponseBody = {
    adjustmentNotes: AdjustmentNote[]
    // What the customer had paid above the new total: it went to their credit in favor
    creditInFavor: number
}

export type HoardPriceCorrectionResponseBody = {
    priceCorrection: HoardPriceCorrection
    creditNoteAmount: number
    adjustmentNote?: AdjustmentNote
}

export type ServerResponse = {
    error?: {
        code?: string
        message: string
    }
    message?: string
    data?: any

}

export class ServerError extends Error {
    constructor(
        public status: number,
        public code: string,
        message: string,
    ) {
        super(message)
    }
}

export type TaxpayerAddressBody = {
    street?: string
    city?: string
    province?: string
    postalCode?: string
}

export type TaxpayerDataBody = {
    identifier?: number
    name?: string
    vatCondition?: VatCondition
    address?: TaxpayerAddressBody
    foundInPadron?: boolean
    cuitValid?: boolean
}

export type CustomerData = {
    identifierType: IdentifierType
    identifier: number
    name: string
    vatCondition: VatCondition
    emails?: string[]
    grossIncomeProvinces?: string[]
    address?: Address
}

export type PreviewBodyItem = {
    name?: string
    productId?: string
    unitTypeId?: string
    unitSubtotal: number
    unitTotal: number
    quantity: number
    totalWithoutVAT: number
    vatAmount: number
}

export type FiscalDocumentPreviewBody = {
    userId: string
    fiscalDocumentId?: string
    fiscalData: {
        documentType: FiscalDocumentType
        documentClass: FiscalDocumentClass
        companyTaxId: number
        saleProvince: string
        fiscalDocumentParentIds?: string[]
    }
    customer: CustomerData
    items: PreviewBodyItem[]
    discounts?: Discount[]
}

export type FiscalDocumentPreviewBodyResponse = {
    fiscalDocumentId: string
    fiscalDocumentDetails: FiscalDocumentDetails
}

export type FiscalDocumentEmitBody = {
    userId: string
    mode?: InvoiceMode
    itemQuantities?: InvoiceItemQuantity[]
    fullInvoice?: boolean
}

export type FiscalDocumentEmitResponseBody = {
    fiscalDocumentId: string
    fiscalDocumentStatus: FiscalDocumentStatus
    message: string
}
